import { ArrowRight } from "lucide-react";

import { CASE_STUDY_PLACEHOLDERS } from "@/content/commercial/case-studies";

export type CaseStudiesSectionProps = {
  variant?: "embedded" | "full";
  showViewAllLink?: boolean;
  viewAllHref?: string;
  viewAllLabel?: string;
  gridSize?: "compact" | "expanded";
};

export function CaseStudiesSection({
  variant = "full",
  showViewAllLink = false,
  viewAllHref = "/commercial-case-studies",
  viewAllLabel = "View case studies",
  gridSize = variant === "embedded" ? "compact" : "expanded",
}: CaseStudiesSectionProps) {
  // Hidden until real case studies are supplied. Placeholder cards must not render publicly.
  const hasRealCaseStudies = CASE_STUDY_PLACEHOLDERS.some(
    (study) => !study.title.trim().startsWith("["),
  );
  if (!hasRealCaseStudies) return null;

  const studies =
    gridSize === "compact" ? CASE_STUDY_PLACEHOLDERS.slice(0, 3) : CASE_STUDY_PLACEHOLDERS;
  const showHeader = variant === "full" || showViewAllLink;



  return (
    <section className="border-b border-border/40 bg-background py-12 md:py-16">
      <div className="container-page">
        {showHeader && (
          <div className="max-w-3xl mb-8 md:mb-10">
            <p className="text-xs font-bold uppercase tracking-widest text-accent mb-2">Case Studies</p>
            <h2 className="text-2xl md:text-4xl font-bold text-foreground leading-tight">Recent Projects</h2>
            <p className="text-sm md:text-base text-muted-foreground mt-3">
              Selected commercial and framework projects with scope, risks managed and outcomes. Real projects
              with before/after photos and buyer outcomes will be added here.
            </p>
          </div>
        )}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {studies.map((study) => (
            <article
              key={study.id}
              className="rounded-2xl border border-dashed border-border bg-card p-6 shadow-sm"
            >
              <div className="aspect-video w-full rounded-lg bg-secondary/40 mb-4 flex items-center justify-center">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  [before/after photos]
                </span>
              </div>
              <h3 className="font-bold text-foreground mb-1">{study.title}</h3>
              <p className="text-xs text-muted-foreground mb-3">
                {study.sector} · {study.location}
              </p>
              <dl className="space-y-2 text-sm text-muted-foreground leading-relaxed">
                <div>
                  <dt className="font-semibold text-foreground text-xs uppercase tracking-wide">Scope</dt>
                  <dd>{study.scope}</dd>
                </div>
                <div>
                  <dt className="font-semibold text-foreground text-xs uppercase tracking-wide">
                    Risks managed
                  </dt>
                  <dd>{study.risksManaged}</dd>
                </div>
                <div>
                  <dt className="font-semibold text-foreground text-xs uppercase tracking-wide">Outcomes</dt>
                  <dd>{study.outcomes}</dd>
                </div>
              </dl>
            </article>
          ))}
        </div>
        {showViewAllLink && (
          <div className="mt-8">
            <a
              href={viewAllHref}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-accent hover:text-accent/80 transition-colors"
            >
              {viewAllLabel}
              <ArrowRight className="h-4 w-4" aria-hidden />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
